import { IWebhookResSchema } from '@monetize/types/app';
import {
  AppColumnDef,
  createColumnHelper,
} from '../../../../components/Monetize/DataTableNew/appTable';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { MdLock } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  useDeleteWebhook,
  useGetWebHooks,
  useUpdateWebhook,
} from '../../../../api/webhooksService';
import {
  MBox,
  MButton,
  MIcon,
  MPageContainer,
  MSettingsPageHeader,
  MText,
  MTooltip,
} from '../../../../components/Monetize';
import DataTableActions, {
  ActionType,
} from '../../../../components/Monetize/DataTable/MDataTableActions';
import { DataTable } from '../../../../components/Monetize/DataTableNew/DataTable';
import { DataTableRef } from '../../../../components/Monetize/DataTableNew/DataTableTypes';
import {
  ACTION_COLUMN_KEY,
  createActionColumn,
} from '../../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import {
  dateTimeBodyTemplate,
  getColHeaderWithSize,
  idBodyTemplate,
  textBodyTemplate,
} from '../../../../components/Monetize/DataTableNew/tableUtilsNew';
import { ROUTES } from '../../../../constants';
import { getWebhookEditRoute } from '../../../../constants/routes';
import { useConfirmModal } from '../../../../services/confirmModal';
import { useDocumentHead } from '../../../../services/documentHead';
import { navigateOrOpenNewWindow } from '../../../../utils/misc';

const columnHelper = createColumnHelper<IWebhookResSchema>();

const getColumns = (
  actionBodyTemplate: (rowData: IWebhookResSchema) => React.ReactNode,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): AppColumnDef<IWebhookResSchema, any>[] => [
  columnHelper.accessor('locked', {
    header: '',
    size: 16,
    cell: ({ row }) => {
      const rowData: IWebhookResSchema = row.original;
      if (rowData.locked) {
        return (
          <MTooltip
            label="This webhook is required for an integration and can't be updated"
            placement="bottom-end"
          >
            <MBox display="inline">
              <MIcon as={MdLock} w={3} h={3} color="tPurple.dark" />
            </MBox>
          </MTooltip>
        );
      }
      return null;
    },
    enableSorting: false,
  }),
  columnHelper.accessor('webhookId', {
    ...getColHeaderWithSize('ID', 275),
    cell: idBodyTemplate(),
    enableSorting: false,
  }),
  columnHelper.accessor('url', {
    ...getColHeaderWithSize('URL', 400),
    cell: textBodyTemplate(),
    enableSorting: false,
  }),
  columnHelper.accessor('createdTime', {
    ...getColHeaderWithSize('Creation Date', 180),
    cell: dateTimeBodyTemplate(),
    enableSorting: false,
  }),
  columnHelper.accessor('enabled', {
    ...getColHeaderWithSize('Status', 120),
    cell: ({ row }) => {
      const rowData: IWebhookResSchema = row.original;
      return (
        <MText lineClamp={1}>{rowData.enabled ? 'Active' : 'Inactive'}</MText>
      );
    },
    enableSorting: false,
  }),
  createActionColumn<IWebhookResSchema>(actionBodyTemplate),
];

const Webhook = () => {
  const { setDocTitle } = useDocumentHead();
  const navigate = useNavigate();

  const { showConfirmModal, setModalLoading, closeModal } = useConfirmModal();
  const tableRef = useRef<DataTableRef>(null);

  const { data: webhooksList, isLoading: loading } = useGetWebHooks();
  const { mutateAsync: updateWebHookMutate } = useUpdateWebhook();
  const { mutateAsync: deleteWebHookMutate } = useDeleteWebhook();
  const navigateToCreate = () => navigate(ROUTES.SETTINGS_WEBHOOK_NEW);

  const handleDeleteWebhook = useCallback(
    (data: IWebhookResSchema) => {
      const onYes = async () => {
        try {
          setModalLoading(true);
          await deleteWebHookMutate(data);
          closeModal();
        } catch (err) {
          handleApiErrorToast(err);
        } finally {
          setModalLoading(false);
        }
      };

      showConfirmModal({
        title: `Are you sure you want to delete "${data.url}"?`,
        description:
          'Once removed, the webhook will no longer be available for use.',
        onYes,
        yesBtnProps: {
          variant: 'delete' as any,
        },
        noBtnProps: {
          variant: 'cancel' as any,
        },
      });
    },
    [closeModal, deleteWebHookMutate, setModalLoading, showConfirmModal],
  );

  useEffect(() => {
    setDocTitle('Settings', 'Webhooks');
  }, []);

  const onSelectEdit = useCallback(
    (id: string, openInNewWindow = false) => {
      openInNewWindow
        ? window.open(getWebhookEditRoute(id), '_blank')
        : navigate(getWebhookEditRoute(id));
    },
    [navigate],
  );

  const actionBodyTemplate = useCallback(
    (rowData: IWebhookResSchema) => {
      const { webhookId } = rowData;
      const actions: ActionType[] = [
        {
          title: 'Edit',
          enabled: !rowData.locked,
          action: () => onSelectEdit(webhookId),
        },
        {
          title: 'Enable',
          enabled: !rowData.enabled,
          action: () =>
            updateWebHookMutate({
              webhookId,
              payload: { ...rowData, enabled: true },
            }),
        },
        {
          title: 'Disable',
          color: 'danger',
          enabled: rowData.enabled && !rowData.locked,
          action: () =>
            updateWebHookMutate({
              webhookId,
              payload: { ...rowData, enabled: false },
            }),
        },
        {
          title: 'Delete',
          color: 'danger',
          enabled: !rowData.locked,
          action: () => handleDeleteWebhook(rowData),
        },
      ];

      return <DataTableActions actions={actions} />;
    },
    [handleDeleteWebhook, onSelectEdit, updateWebHookMutate],
  );

  const columns = useMemo(
    () => getColumns(actionBodyTemplate),
    [actionBodyTemplate],
  );

  return (
    <MPageContainer alignItems="stretch">
      <MSettingsPageHeader title="Webhooks">
        {!!webhooksList?.length && (
          <MButton variant="primary" onClick={navigateToCreate}>
            New
          </MButton>
        )}
      </MSettingsPageHeader>

      <DataTable
        ref={tableRef}
        columns={columns}
        data={webhooksList ?? []}
        isLoading={loading}
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        enablePagination={false}
        onRowClick={(row, _tableRow, openInNewWindow) =>
          navigateOrOpenNewWindow(
            navigate,
            getWebhookEditRoute(row.webhookId),
            openInNewWindow,
          )
        }
        emptyProps={{
          mainMessage: 'Looks like there are no webhooks here.',
          btnLabel: 'New Webhook',
          to: ROUTES.SETTINGS_WEBHOOK_NEW,
        }}
      />
    </MPageContainer>
  );
};

export default Webhook;
